import { ReactElement, ReactNode } from 'react';
import { ControlledTableProps, KeyGetter, RowType, SizeProps } from './types';
import { ControlledTable } from './ControlledTable';
import { Box, TableColumns } from '@ff-it/ui';
import { TableFilter } from './TableFilter';
import { useSessionScope } from 'services';

export type IndexTableProps<R extends RowType> = {
  url: string;
  filterFields?: ReactNode;
  columns: TableColumns<R>;
  before?: ReactNode;
  footer?: ReactNode;
  after?: ReactNode;
  // This is convoluted but we use null to disable storage
  storageKey?: string | null;
  pageSize?: number;
} & Pick<ControlledTableProps<R>, 'defaultFilter' | 'queryParams' | 'renderTable' | 'tableProps'>;

export function IndexTable<R extends RowType>({
  url,
  filterFields,
  before,
  // use url as storageKey if we have filters
  // FIXME: probably not safe
  storageKey = filterFields !== null ? url : undefined,
  pageSize: providedPageSize,
  after,
  footer,
  tableProps,
  ...rest
}: IndexTableProps<R>): ReactElement {
  const { slug } = useSessionScope();
  const getKey: KeyGetter = ({ filter, pageSize, pageIndex, sort, queryParams }) => {
    return {
      url,
      method: 'GET',
      queryParams: {
        // FIXME
        ...filter,
        page_size: pageSize,
        page: pageIndex,
        ordering: sort ? `${sort.direction === 'DESC' ? '-' : ''}${sort.key.replace('.', '__')}` : undefined,
        ...queryParams,
      },
      _slug: slug,
    };
  };

  const sizeProps: SizeProps = providedPageSize
    ? {
        pageSize: providedPageSize,
      }
    : {
        defaultPageSize: 30,
      };

  return (
    <ControlledTable<R>
      // remount
      key={url}
      {...sizeProps}
      getKey={getKey}
      storageKey={storageKey === null ? undefined : storageKey}
      tableProps={{
        size: 'sm',
        fontSize: 'sm',
        hover: true,
        // striped: true,
        // size: 'xs',
        fixed: true,
        footer,
        before: (
          <>
            {before}
            {filterFields !== null && (
              <Box display="flex" mt="2">
                <TableFilter>{filterFields}</TableFilter>
              </Box>
            )}
          </>
        ),
        after,
        ...tableProps,
      }}
      {...rest}
    />
  );
}
